import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Timestamp } from "firebase/firestore";
import { ExpertLetterDraft } from "../../types/expert-letter-draft";
import { ExtractionStatus } from "@/types/extractionStatus";

export interface LegalBriefSection {
  id?: string;
  text?: string;
  section_name?: string;
  display_name?: string;
  sections_after_review?: string;
  status?: ExtractionStatus;
}

export interface ExhibitListDraft {
  isExtracting?: any;
  isProcessing?: any;
  needs_update?: boolean;
  status?: ExtractionStatus;
  generated_at?: Timestamp;
}
export interface DraftType {
  id?: string;
  value?: any;
  date?: any;
  accessEnabled?: boolean;
  sections?: LegalBriefSection[] | null;
  generated_text?: string | null;
  needs_update?: boolean;
  status?: ExtractionStatus;
  generated_at?: Timestamp;
}

export interface PacketData {
  filePath?: string;
  docUrl?: string;
  generated_at?: Timestamp;
  status?: ExtractionStatus;
}

export interface DraftsState {
  legalBrief?: DraftType | null;
  personalStatement?: DraftType | null;
  certifiedStatement?: DraftType | null;
  asylumAffidavit?: DraftType | null;
  hardshipStatementBeneficiary?: DraftType | null;
  hardshipStatementPetitioner?: DraftType | null;
  coverLetter?: DraftType | null;
  expertLetters?: Record<string, ExpertLetterDraft> | null;
  exhibitList: ExhibitListDraft | null;
  packet?: PacketData | null;
}

const initialState: DraftsState = {
  legalBrief: null,
  personalStatement: null,
  certifiedStatement: null,
  asylumAffidavit: null,
  hardshipStatementBeneficiary: null,
  hardshipStatementPetitioner: null,
  coverLetter: null,
  expertLetters: null,
  exhibitList: null,
  packet: null
};

export interface DraftsReducer {
  drafts: DraftsState;
}

export const draftsSlice = createSlice({
  name: "drafts",
  initialState,
  reducers: {
    setLegalBrief: (state, action: PayloadAction<DraftType | null>) => {
      state.legalBrief = action.payload;
    },
    setPersonalStatement: (state, action: PayloadAction<DraftType | null>) => {
      state.personalStatement = action.payload;
    },
    setCertifiedStatement: (state, action: PayloadAction<DraftType | null>) => {
      state.certifiedStatement = action.payload;
    },
    setAsylumAffidavit: (state, action: PayloadAction<DraftType | null>) => {
      state.asylumAffidavit = action.payload;
    },
    setHardshipStatementBeneficiary: (
      state,
      action: PayloadAction<DraftType | null>
    ) => {
      state.hardshipStatementBeneficiary = action.payload;
    },
    setHardshipStatementPetitioner: (
      state,
      action: PayloadAction<DraftType | null>
    ) => {
      state.hardshipStatementPetitioner = action.payload;
    },
    setCoverLetter: (state, action: PayloadAction<DraftType | null>) => {
      state.coverLetter = action.payload;
    },
    setExpertLettersDrafts: (
      state,
      action: PayloadAction<Record<string, ExpertLetterDraft>>
    ) => {
      state.expertLetters = action.payload;
    },
    setExhibitList: (state, action: PayloadAction<any | null>) => {
      state.exhibitList = action.payload;
    },
    setPacket: (state, action: PayloadAction<PacketData | null>) => {
      state.packet = action.payload;
    },
    updatePacketUrl: (state, action: PayloadAction<{ docUrl: string }>) => {
      if (state.packet) {
        state.packet.docUrl = action.payload.docUrl;
      }
    },
    clearLegalBrief: (state) => {
      state.legalBrief = null;
    },
    clearPersonalStatement: (state) => {
      state.personalStatement = null;
    },
    clearCertifiedStatement: (state) => {
      state.certifiedStatement = null;
    },
    clearAsylumAffidavit: (state) => {
      state.asylumAffidavit = null;
    },
    clearHardshipStatementBeneficiary: (state) => {
      state.hardshipStatementBeneficiary = null;
    },
    clearHardshipStatementPetitioner: (state) => {
      state.hardshipStatementPetitioner = null;
    },
    clearCoverLetter: (state) => {
      state.coverLetter = null;
    },
    clearPacket: (state) => {
      state.packet = null;
    }
  }
});

export const {
  clearLegalBrief,
  setLegalBrief,
  setPersonalStatement,
  clearPersonalStatement,
  setCertifiedStatement,
  clearCertifiedStatement,
  setAsylumAffidavit,
  clearAsylumAffidavit,
  setHardshipStatementBeneficiary,
  clearHardshipStatementBeneficiary,
  setHardshipStatementPetitioner,
  clearHardshipStatementPetitioner,
  setCoverLetter,
  clearCoverLetter,
  setExpertLettersDrafts,
  setExhibitList,
  setPacket,
  updatePacketUrl,
  clearPacket
} = draftsSlice.actions;

export default draftsSlice.reducer;
